import React, { useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/App";
import axios from "axios";
import { 
  Calendar,
  TrendingUp,
  Search,
  RefreshCw,
  Plus,
  X,
  ArrowRight,
  BarChart3
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";

const FundSearchInput = ({ value, onChange, onSelect, placeholder }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);

  const searchFunds = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/funds/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data.funds || []);
      setShowDropdown(true);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(() => searchFunds(value), 300);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder={placeholder || "Search fund..."}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
          className="pl-10"
        />
        {searching && (
          <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
      </div>
      
      {showDropdown && searchResults.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-48 overflow-auto">
          {searchResults.slice(0, 8).map((fund) => (
            <button
              key={fund.schemeCode}
              className="w-full text-left px-4 py-2 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0"
              onClick={() => {
                onSelect(fund);
                setShowDropdown(false);
              }}
            >
              <div className="font-medium text-sm truncate">{fund.schemeName}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const COLORS = ['#F59E0B', '#3B82F6', '#22C55E', '#EF4444', '#8B5CF6'];

export const PointToPointPage = () => {
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [searchQueries, setSearchQueries] = useState(['', '', '', '']);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSelectFund = (fund, index) => {
    const newFunds = [...selectedFunds];
    newFunds[index] = fund;
    setSelectedFunds(newFunds);
    
    const newQueries = [...searchQueries];
    newQueries[index] = fund.schemeName;
    setSearchQueries(newQueries);
  };

  const handleRemoveFund = (index) => {
    const newFunds = [...selectedFunds];
    newFunds[index] = null;
    setSelectedFunds(newFunds);
    
    const newQueries = [...searchQueries];
    newQueries[index] = '';
    setSearchQueries(newQueries);
  };

  const compareReturns = async () => {
    const validFunds = selectedFunds.filter(f => f);
    if (validFunds.length === 0 || !startDate || !endDate) return;
    
    setLoading(true);
    try {
      const codes = validFunds.map(f => f.schemeCode).join(',');
      const response = await axios.get(
        `${API}/funds/analytics/point-to-point?scheme_codes=${codes}&start_date=${startDate}&end_date=${endDate}`
      );
      setResults(response.data);
    } catch (error) {
      console.error("Error comparing:", error);
    } finally {
      setLoading(false);
    }
  };

  // Preset date ranges
  const setPresetRange = (years) => {
    const end = new Date();
    const start = new Date();
    start.setFullYear(start.getFullYear() - years);
    
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  };

  const formatDate = (date) => {
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const chartData = results?.comparison?.map((fund, idx) => ({
    name: fund.scheme_name.substring(0, 20) + '...',
    cagr: fund.cagr,
    absolute: fund.absolute_return,
    color: COLORS[idx % COLORS.length]
  })) || [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="point-to-point-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Calendar className="w-8 h-8 text-primary" />
            Point-to-Point Returns
          </h1>
          <p className="text-muted-foreground mt-1">
            Compare fund returns between specific dates
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Panel */}
          <Card className="bg-card/50 border-border/50 lg:col-span-1">
            <CardHeader>
              <CardTitle>Compare Funds</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Fund Selection */}
              {[0, 1, 2, 3].map((index) => (
                <div key={index} className="space-y-1">
                  <Label className="text-xs">Fund {index + 1}</Label>
                  <div className="relative">
                    <FundSearchInput
                      value={searchQueries[index]}
                      onChange={(v) => {
                        const newQueries = [...searchQueries];
                        newQueries[index] = v;
                        setSearchQueries(newQueries);
                      }}
                      onSelect={(fund) => handleSelectFund(fund, index)}
                      placeholder={`Search fund ${index + 1}...`}
                    />
                    {selectedFunds[index] && (
                      <button
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                        onClick={() => handleRemoveFund(index)}
                      >
                        <X className="w-4 h-4 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Date Range */}
              <div className="pt-4 border-t border-border/50 space-y-3">
                <Label>Date Range</Label>
                <div className="flex gap-2">
                  {[1, 3, 5, 10].map(y => (
                    <Button
                      key={y}
                      variant="outline"
                      size="sm"
                      onClick={() => setPresetRange(y)}
                    >
                      {y}Y
                    </Button>
                  ))}
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Start Date</Label>
                    <Input
                      placeholder="DD-MM-YYYY"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">End Date</Label>
                    <Input
                      placeholder="DD-MM-YYYY"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={compareReturns}
                disabled={loading || selectedFunds.filter(f => f).length === 0 || !startDate || !endDate}
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TrendingUp className="w-4 h-4 mr-2" />
                )}
                Compare Returns
              </Button>
            </CardContent>
          </Card>

          {/* Results Panel */}
          <div className="lg:col-span-2 space-y-6">
            {results ? (
              <>
                {/* Chart */}
                <Card className="bg-card/50 border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      CAGR Comparison
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis type="number" stroke="#9CA3AF" tickFormatter={(v) => `${v}%`} />
                          <YAxis type="category" dataKey="name" stroke="#9CA3AF" width={150} tick={{ fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #374151' }}
                            labelStyle={{ color: '#F8FAFC' }}
                            formatter={(value) => [`${value.toFixed(2)}%`, 'CAGR']}
                          />
                          <Bar dataKey="cagr" radius={[0, 4, 4, 0]}>
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Detailed Results Table */}
                <Card className="bg-card/50 border-border/50">
                  <CardHeader>
                    <CardTitle>Detailed Comparison</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Period: {results.period.start} to {results.period.end}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3 font-medium">Fund Name</th>
                            <th className="text-right p-3 font-medium">Start NAV</th>
                            <th className="text-right p-3 font-medium">End NAV</th>
                            <th className="text-right p-3 font-medium">Absolute Return</th>
                            <th className="text-right p-3 font-medium">CAGR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.comparison.map((fund, idx) => (
                            <tr key={fund.scheme_code} className="border-t border-border/50">
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                                  />
                                  <span className="font-medium text-sm">{fund.scheme_name}</span>
                                </div>
                              </td>
                              <td className="p-3 text-right">₹{fund.start_nav}</td>
                              <td className="p-3 text-right">₹{fund.end_nav}</td>
                              <td className={`p-3 text-right font-bold ${fund.absolute_return >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {fund.absolute_return >= 0 ? '+' : ''}{fund.absolute_return}%
                              </td>
                              <td className={`p-3 text-right font-bold ${fund.cagr >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {fund.cagr >= 0 ? '+' : ''}{fund.cagr}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold">Compare Point-to-Point Returns</h3>
                  <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                    Select up to 4 funds and a date range to compare their returns side by side.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
